import {
  Issue,
  addIssue,
  removeErrorDescriptionById,
  removeIssueById,
  updateErrorDescriptionId,
} from "./appState/issuesSlice";
import {
  Middleware,
  MiddlewareAPI,
  isFulfilled,
  isRejectedWithValue,
} from "@reduxjs/toolkit";

import { AppDispatch } from "./store";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getErrorText } from "../utils/errors";
import { logEvent } from "./api/apiSlice";
import { patientApiSlice } from "./data/patientApiSlice";

const logIssueEndpoints = ["sendHCWMessage", "sendHCWTemplate"];

export const rtkQueryIssueLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    // Handle logging of issues based on endpoint name
    if (
      isRejectedWithValue(action) &&
      action.meta.requestId &&
      action.meta.arg &&
      logIssueEndpoints.includes((action.meta.arg as any).endpointName)
    ) {
      if (action.payload && "status" in (action.payload as object)) {
        const errorPayload = action.payload as FetchBaseQueryError;
        const newIssue: Issue =
          typeof errorPayload.status === "number" && errorPayload.status < 500
            ? {
                // Status code is less than 500, so we'll consider it non-retryable
                id: action.meta.requestId,
                type: "error",
                description: [
                  api.getState().issues.errorDescriptions[
                    action.meta.requestId
                  ] || "",
                  getErrorText(action.payload as FetchBaseQueryError),
                ].join(" "),
              }
            : {
                // Status code is 500 or higher, so we'll consider it retryable
                id: action.meta.requestId,
                type: "error",
                description:
                  api.getState().issues.errorDescriptions[
                    action.meta.requestId
                  ] || "Unexpected: no error description was set",
                retryData: {
                  endpointName: (action.meta.arg as any).endpointName,
                  originalArgs: JSON.stringify(
                    (action.meta.arg as any).originalArgs
                  ),
                  requestId: action.meta.requestId,
                },
              };
        api.dispatch(addIssue(newIssue));
        logEvent(api.dispatch, {
          event: "issueOccurred",
          data: {
            issueId: newIssue.id,
            error: JSON.stringify(errorPayload),
            canRetry: !!newIssue.retryData,
          },
        });
      }
    } else if (
      isFulfilled(action) &&
      action.meta.requestId &&
      action.meta.arg &&
      logIssueEndpoints.includes((action.meta.arg as any).endpointName)
    ) {
      // Remove the error description if it was fulfilled
      api.dispatch(removeErrorDescriptionById(action.meta.requestId));
    }
    return next(action);
  };

export const retryIssue = (dispatch: AppDispatch, issue: Issue) => {
  if (issue?.retryData) {
    const newRequest = dispatch(
      (patientApiSlice.endpoints as any)[issue.retryData.endpointName].initiate(
        JSON.parse(issue.retryData.originalArgs),
        { track: false }
      )
    );
    dispatch(
      updateErrorDescriptionId({
        from: issue.retryData.requestId,
        to: newRequest.requestId,
      })
    );
    dispatch(removeIssueById(issue.id));
    logEvent(dispatch, {
      event: "issueRetried",
      data: {
        issueId: issue.id,
      },
    });
  }
};
